/* ==========================================================================
   IvermectinReport.com — theme.js

   The only script in the theme. Deferred, no dependencies, no analytics, no
   tracking, no third-party code, no cart logic, no fetch calls.

   Jobs, in order:
     1. Build the table of contents from the rendered h2s in the body.
     2. Highlight the current section in the table of contents.
     3. Collapse the table of contents into its disclosure on narrow screens.
     4. Wrap wide tables so they scroll instead of breaking the measure.
     5. Normalise rel= on external links.
     6. Emit FAQPage JSON-LD from the rendered DOM (Liquid cannot parse the
        body HTML, so this has to happen client side).
     7. Light/dark theme toggle.
     8. Mobile navigation disclosure.
     9. Optional state-board filter, when the owner has mapped status tags.
    11. Video chapters: ?t=SECONDS in the URL, or a click on a [data-seek]
        link, moves the page's native <video> to that second. No player
        library; the element is a plain HTML5 video.
    10. Share row: copy-to-clipboard and, where the browser has it, the native
        share sheet. Both are browser APIs. The share links themselves are
        plain anchors in the markup, so nothing is requested from any third
        party unless the reader actually clicks one.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ utils */

  function slugify(text) {
    return String(text)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';
  }

  function uniqueId(base, used) {
    var id = base;
    var n = 2;
    while (used[id] || document.getElementById(id)) {
      id = base + '-' + n;
      n += 1;
    }
    used[id] = true;
    return id;
  }

  function text(node) {
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  var NARROW = '(max-width: 1079.98px)';

  /* ----------------------------------------------------------- 1. TOC build */

  function buildToc() {
    var container = document.querySelector('[data-toc]');
    var body = document.querySelector('[data-article-body]');
    if (!container || !body) return;

    var list = container.querySelector('[data-toc-list]');
    if (!list || list.children.length) return; // already built

    var min = parseInt(container.getAttribute('data-toc-min') || '3', 10);
    var headings = Array.prototype.slice.call(body.querySelectorAll('h2'));

    // Only headings that actually carry text.
    headings = headings.filter(function (h) { return text(h).length > 1; });

    if (headings.length < min) return;

    var used = {};
    var frag = document.createDocumentFragment();

    headings.forEach(function (h) {
      var label = text(h);
      if (!h.id) h.id = uniqueId(slugify(label), used);

      var li = document.createElement('li');
      var a = document.createElement('a');
      var span = document.createElement('span');
      a.href = '#' + h.id;
      span.textContent = label;
      a.appendChild(span);
      li.appendChild(a);
      frag.appendChild(li);
    });

    list.appendChild(frag);

    // The shell is hidden until it has something in it, so a short page never
    // shows an empty box and a visitor without JS sees nothing at all.
    container.hidden = false;
    var shell = container.closest('[data-toc-shell]');
    if (shell) shell.hidden = false;

    markTocCurrent();
    collapseTocOnNarrow();
  }

  /* -------------------------------------------------- 2. TOC current section */

  function markTocCurrent() {
    var links = Array.prototype.slice.call(document.querySelectorAll('[data-toc-list] a'));
    if (!links.length || !('IntersectionObserver' in window)) return;

    var map = {};
    links.forEach(function (l) {
      var target = document.getElementById((l.getAttribute('href') || '').slice(1));
      if (target) map[target.id] = l;
    });

    var ids = Object.keys(map);
    if (!ids.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        links.forEach(function (l) { l.classList.remove('is-current'); });
        if (map[entry.target.id]) map[entry.target.id].classList.add('is-current');
      });
    }, { rootMargin: '-70px 0px -72% 0px', threshold: 0 });

    ids.forEach(function (id) { io.observe(document.getElementById(id)); });
  }

  /* ---------------------------------------------- 3. TOC disclosure on small */

  // The markup ships the <details> OPEN so that the contents are present and
  // readable with JavaScript disabled. On narrow screens, where the rail is
  // not a rail, it is collapsed to a tappable summary instead.
  function collapseTocOnNarrow() {
    var shell = document.querySelector('[data-toc-shell]');
    if (!shell || shell.dataset.userToggled === 'true') return;

    try {
      if (window.matchMedia(NARROW).matches) shell.removeAttribute('open');
    } catch (e) { /* no matchMedia: leave it open */ }

    if (!shell.dataset.bound) {
      shell.dataset.bound = 'true';
      shell.addEventListener('toggle', function () { shell.dataset.userToggled = 'true'; });
    }
  }

  /* ------------------------------------------------------- 4. Table wrapping */

  function wrapTables() {
    var bodies = document.querySelectorAll('[data-article-body], [data-utility-body]');

    Array.prototype.forEach.call(bodies, function (body) {
      var tables = body.querySelectorAll('table');
      Array.prototype.forEach.call(tables, function (table) {
        var parent = table.parentNode;
        if (parent && parent.classList && parent.classList.contains('table-scroll')) return;
        var wrap = document.createElement('div');
        wrap.className = 'table-scroll';
        wrap.setAttribute('tabindex', '0');
        wrap.setAttribute('role', 'group');
        wrap.setAttribute('aria-label', 'Table, scrolls horizontally');
        parent.insertBefore(wrap, table);
        wrap.appendChild(table);
      });
    });
  }

  /* -------------------------------------------------- 5. External link rel= */

  function markExternalLinks() {
    var host = window.location.hostname;
    var links = document.querySelectorAll('main a[href]');

    Array.prototype.forEach.call(links, function (a) {
      var href = a.getAttribute('href') || '';
      if (!/^https?:/i.test(href)) return;

      var linkHost;
      try {
        linkHost = new URL(href, window.location.href).hostname;
      } catch (e) {
        return;
      }
      if (!linkHost || linkHost === host) return;

      var rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (rel.indexOf('noopener') === -1) rel.push('noopener');
      // Only strip the referrer when the link opens a new context.
      if (a.target === '_blank' && rel.indexOf('noreferrer') === -1) rel.push('noreferrer');
      a.setAttribute('rel', rel.join(' '));

      a.classList.add('is-external');
    });
  }

  /* ------------------------------------------------- 6. FAQPage JSON-LD */

  // Default match for the h2 that opens the FAQ block in the generated body
  // HTML. snippets/schema-faq.liquid can override the wording.
  var FAQ_HEADING = /frequently\s+asked\s+questions|^faqs?$/i;

  function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Reads the config emitted by snippets/schema-faq.liquid.
  // Returns null when the snippet was not rendered or the feature is off.
  function faqConfig() {
    var el = document.querySelector('script[type="application/json"][data-faq-config]');
    if (!el) return null;

    var cfg;
    try {
      cfg = JSON.parse(el.textContent || '{}');
    } catch (e) {
      return { pattern: FAQ_HEADING };
    }
    if (cfg.enabled === false) return null;

    return {
      pattern: cfg.heading
        ? new RegExp(escapeRegExp(cfg.heading), 'i')
        : FAQ_HEADING
    };
  }

  function collectFaq(body, pattern) {
    var headings = Array.prototype.slice.call(body.querySelectorAll('h2'));
    var start = null;

    for (var i = 0; i < headings.length; i += 1) {
      if (pattern.test(text(headings[i]))) { start = headings[i]; break; }
    }
    if (!start) return [];

    var pairs = [];
    var node = start.nextElementSibling;
    var current = null;

    while (node && node.tagName !== 'H2') {
      if (node.tagName === 'H3') {
        if (current && current.answer.trim()) pairs.push(current);
        current = { question: text(node), answer: '' };
      } else if (current) {
        var chunk = text(node);
        if (chunk) current.answer += (current.answer ? ' ' : '') + chunk;
      }
      node = node.nextElementSibling;
    }
    if (current && current.answer.trim()) pairs.push(current);

    return pairs.filter(function (p) { return p.question && p.answer; });
  }

  function injectFaqSchema() {
    var body = document.querySelector('[data-article-body]');
    if (!body) return;
    if (document.querySelector('script[data-faq-schema]')) return; // already emitted

    var cfg = faqConfig();
    if (!cfg) return;

    var pairs = collectFaq(body, cfg.pattern);
    if (!pairs.length) return; // Only emit when at least one Q/A pair exists.

    var data = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: pairs.map(function (p) {
        return {
          '@type': 'Question',
          name: p.question,
          acceptedAnswer: { '@type': 'Answer', text: p.answer }
        };
      })
    };

    var script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-faq-schema', '');
    script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
    document.head.appendChild(script);
  }

  /* --------------------------------------------------------- 7. Theme toggle */

  var STORE_KEY = 'wtbi-theme';

  function themeToggle() {
    var root = document.documentElement;
    var btn = document.querySelector('[data-theme-toggle]');
    var media = null;

    try { media = window.matchMedia('(prefers-color-scheme: dark)'); } catch (e) { /* ignore */ }

    var stored = null;
    try { stored = window.localStorage.getItem(STORE_KEY); } catch (e) { /* private mode */ }
    if (stored === 'dark' || stored === 'light') root.setAttribute('data-theme', stored);

    if (!btn) return;

    function isDark() {
      var attr = root.getAttribute('data-theme');
      if (attr) return attr === 'dark';
      return !!(media && media.matches);
    }

    function sync() {
      var dark = isDark();
      btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
      btn.setAttribute('aria-label', dark ? 'Switch to the light theme' : 'Switch to the dark theme');
      var label = btn.querySelector('[data-theme-label]');
      if (label) label.textContent = dark ? 'Light' : 'Dark';
    }

    btn.addEventListener('click', function () {
      var next = isDark() ? 'light' : 'dark';
      root.setAttribute('data-theme', next);
      try { window.localStorage.setItem(STORE_KEY, next); } catch (e) { /* ignore */ }
      sync();
    });

    if (media && media.addEventListener) {
      media.addEventListener('change', function () {
        if (!root.getAttribute('data-theme')) sync();
      });
    }

    sync();
  }

  /* ------------------------------------------------------------ 8. Mobile nav */

  function navToggle() {
    var btn = document.querySelector('[data-nav-toggle]');
    if (!btn) return;
    var nav = document.getElementById(btn.getAttribute('aria-controls') || '');
    if (!nav) return;

    btn.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------- 9. State filtering */

  function stateFilter() {
    var board = document.querySelector('[data-stateboard]');
    if (!board) return;

    var group = document.querySelector('[data-state-filters]');
    if (!group) return;

    var buttons = Array.prototype.slice.call(group.querySelectorAll('button[data-filter]'));
    if (!buttons.length) return;

    var live = document.querySelector('[data-state-count]');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var want = btn.getAttribute('data-filter');
        var shown = 0;

        buttons.forEach(function (other) {
          other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
        });

        Array.prototype.forEach.call(board.children, function (tile) {
          var show = want === 'all' || tile.getAttribute('data-status') === want;
          tile.hidden = !show;
          if (show) shown += 1;
        });

        if (live) live.textContent = shown + ' of ' + board.children.length + ' shown';
      });
    });
  }

  /* ------------------------------------------------------------------ share */

  function shareRow() {
    var box = document.querySelector('[data-share]');
    if (!box) return;

    var durum = box.querySelector('[data-share-status]');
    var zaman;

    function bildir(metin) {
      if (!durum) return;
      durum.textContent = metin;
      window.clearTimeout(zaman);
      zaman = window.setTimeout(function () { durum.textContent = ''; }, 4000);
    }

    /* Kopyala. navigator.clipboard yalnizca guvenli baglamda var; yoksa
       eski secme-ve-kopyala yolu kullaniliyor, boylece dugme hicbir
       tarayicida olu kalmiyor. */
    var kopyaBtn = box.querySelector('[data-share-copy]');
    if (kopyaBtn) {
      kopyaBtn.addEventListener('click', function () {
        var adres = kopyaBtn.getAttribute('data-share-url') || window.location.href;
        function oldu() { bildir('Link copied'); }
        function olmadi() { bildir('Could not copy. The address is in your browser bar.'); }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(adres).then(oldu, olmadi);
          return;
        }
        try {
          var ta = document.createElement('textarea');
          ta.value = adres;
          ta.setAttribute('readonly', '');
          ta.style.cssText = 'position:absolute;left:-9999px;top:0';
          document.body.appendChild(ta);
          ta.select();
          var ok = document.execCommand('copy');
          document.body.removeChild(ta);
          if (ok) { oldu(); } else { olmadi(); }
        } catch (e) { olmadi(); }
      });
    }

    /* Telefonun kendi paylasim sayfasi. Yalnizca tarayicida varsa eklenir;
       masaustunde dugme hic basilmaz, cunku orada calismayan bir dugme
       gostermek kullaniciyi bosa tiklatir. */
    if (navigator.share) {
      var liste = box.querySelector('.share__list');
      var kaynak = kopyaBtn;
      if (liste && kaynak) {
        var li = document.createElement('li');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'share__btn';
        b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
          + '<path fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"'
          + ' stroke-linejoin="round" d="M12 3v13M8 6.5 12 2.5l4 4M5 13v6.5h14V13"/></svg>'
          + '<span>Share</span>';
        b.addEventListener('click', function () {
          navigator.share({
            title: document.title,
            url: kaynak.getAttribute('data-share-url') || window.location.href
          }).catch(function () { /* kullanici vazgecti: sessiz */ });
        });
        li.appendChild(b);
        liste.insertBefore(li, liste.firstChild);
      }
    }
  }

  /* ---------------------------------------------------------- video seek */

  function videoSeek() {
    var v = document.querySelector('[data-video-player]');
    if (!v) return;

    function go(sec, play) {
      if (isNaN(sec)) return;
      function set() {
        try { v.currentTime = sec; } catch (e) { /* metadata yok */ }
        if (play) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      }
      if (v.readyState >= 1) { set(); }
      else {
        v.preload = 'metadata';
        v.addEventListener('loadedmetadata', set, { once: true });
        v.load();
      }
    }

    // Google "onemli anlar" baglantisi: sayfa ?t=63 ile acilirsa oraya sar.
    var m = /[?&]t=(\d+)/.exec(window.location.search);
    if (m) {
      go(parseInt(m[1], 10), false);
      v.scrollIntoView({ block: 'center' });
    }

    Array.prototype.slice.call(document.querySelectorAll('[data-seek]')).forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var sec = parseInt(a.getAttribute('data-seek'), 10);
        go(sec, true);
        v.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (window.history && history.replaceState) {
          history.replaceState(null, '', window.location.pathname + '?t=' + sec);
        }
      });
    });
  }

  /* -------------------------------------------------------- video facade */

  // Article pages carry only a poster button; the <video> is created on
  // click so Google sees the video on its watch page alone.
  function videoFacade() {
    Array.prototype.slice.call(document.querySelectorAll('[data-video-facade]')).forEach(function (b) {
      b.addEventListener('click', function () {
        var v = document.createElement('video');
        v.controls = true;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.preload = 'auto';
        if (b.getAttribute('data-poster')) v.poster = b.getAttribute('data-poster');
        v.setAttribute('aria-label', b.getAttribute('aria-label') || 'Video');
        v.src = b.getAttribute('data-src');
        b.parentNode.replaceChild(v, b);
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }, { once: true });
    });
  }

  /* ------------------------------------------------------------------- init */

  function safe(fn) {
    try { fn(); } catch (e) { /* non-fatal: never let one feature break the page */ }
  }

  // The theme attribute is restored first so the flip happens as early as this
  // deferred script can manage it.
  safe(themeToggle);

  function init() {
    safe(buildToc);
    safe(wrapTables);
    safe(markExternalLinks);
    safe(injectFaqSchema);
    safe(navToggle);
    safe(stateFilter);
    safe(shareRow);
    safe(videoSeek);
    safe(videoFacade);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The Shopify theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', function () {
    safe(buildToc);
    safe(wrapTables);
    safe(markExternalLinks);
    safe(navToggle);
    safe(stateFilter);
    safe(themeToggle);
  });

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () { safe(collapseTocOnNarrow); }, 180);
  });
})();

/* ---------------------------------------------------------- law tracker
   Filters the state table in place and labels each cell for the stacked
   mobile view. Progressive: with no JS the full table still renders and is
   fully readable, it just cannot be filtered. */
(function () {
  var root = document.querySelector('[data-tracker]');
  if (!root) return;
  var table = root.querySelector('.tracker__table');
  var box = root.querySelector('[data-tracker-filters]');
  if (!table || !box) return;

  var rows = Array.prototype.slice.call(table.querySelectorAll('tbody tr'));
  if (!rows.length) return;

  // stacked mobile view needs each cell to name its own column
  var heads = Array.prototype.slice.call(table.querySelectorAll('thead th'))
    .map(function (th) { return th.textContent.trim(); });
  rows.forEach(function (tr) {
    Array.prototype.slice.call(tr.children).forEach(function (cell, i) {
      if (cell.tagName === 'TD' && heads[i]) cell.setAttribute('data-label', heads[i]);
    });
  });

  var GROUPS = [
    { key: 'all', label: 'All 51', test: function () { return true; } },
    { key: 'access', label: 'Access laws', test: function (tr) {
        var m = tr.getAttribute('data-mech');
        return tr.getAttribute('data-status') === 'in_force' &&
          ['otc_statutory', 'pharmacist_protocol', 'pharmacist_cpa', 'standing_order'].indexOf(m) > -1;
      } },
    { key: 'otc', label: 'Over-the-counter', test: function (tr) {
        return tr.getAttribute('data-mech') === 'otc_statutory'; } },
    { key: 'pharmacist', label: 'Pharmacist route', test: function (tr) {
        var m = tr.getAttribute('data-mech');
        return m === 'pharmacist_protocol' || m === 'pharmacist_cpa' || m === 'standing_order'; } },
    { key: 'shield', label: 'Protection only', test: function (tr) {
        return tr.getAttribute('data-mech') === 'shield_only'; } },
    { key: 'pending', label: 'Bill pending', test: function (tr) {
        return tr.getAttribute('data-status') === 'pending'; } },
    { key: 'none', label: 'No law', test: function (tr) {
        return tr.getAttribute('data-mech') === 'none'; } }
  ];

  var count = document.createElement('span');
  count.className = 'tracker__count';
  var current = 'all';

  function apply(key) {
    var g = GROUPS.filter(function (x) { return x.key === key; })[0] || GROUPS[0];
    var n = 0;
    rows.forEach(function (tr) {
      var show = g.test(tr);
      tr.hidden = !show;
      if (show) n++;
    });
    current = key;
    count.textContent = n + (n === 1 ? ' jurisdiction' : ' jurisdictions');
    Array.prototype.slice.call(box.querySelectorAll('button')).forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-key') === key));
    });
  }

  GROUPS.forEach(function (g) {
    var b = document.createElement('button');
    b.type = 'button';
    b.textContent = g.label;
    b.setAttribute('data-key', g.key);
    b.setAttribute('aria-pressed', String(g.key === 'all'));
    b.addEventListener('click', function () { apply(g.key); });
    box.appendChild(b);
  });
  box.appendChild(count);
  apply('all');
})();
